import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount, AssetRatio, IAssetAmountMetadata } from "@sundaeswap/asset";
import { EContractVersion } from "@sundaeswap/core";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import { getFirstLp } from "@sundaeswap/cpp";
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { usePoolsByPair } from "../../../gql/hooks/pools.query";
import { IAssetMetaData } from "../../../types/Asset.types";
import { Actions, useAppContext } from "../../AppContext";
import { EFeeTiers, ELiquidityFlowState, useLiquidityContext } from "../context";

export enum ECreatePoolWizardStep {
  entry = "entry",
  contractVersion = "contractVersion",
  feeTier = "feeTier",
  confirm = "confirm",
  marketOpen = "marketOpen",
}

export enum EMarketTimingState {
  default = "default",
  custom = "custom",
}

export interface ICreatePoolContext {
  derived: {
    amountOfLpTokens: bigint | undefined;
    aPerB: string;
    assetAmountA: AssetAmount<IAssetAmountMetadata | IAssetMetaData>;
    assetAmountB: AssetAmount<IAssetAmountMetadata | IAssetMetaData>;
    bPerA: string;
    isFetchingPoolsByPair: boolean;
    multiplePoolsExistForPair: boolean;
    poolAlreadyExistsForFeeTier: boolean;
    somePoolsExistForPair: boolean;
    anyPoolExitForPair: boolean;
  };
  handlers: {
    handleBackToInitialAssetSelect: () => void;
    handleCloseCreatePoolDialog(): void;
    setFee: Dispatch<SetStateAction<ICreatePoolContext["state"]["fee"] | undefined>>;
    setMarketOpen: Dispatch<SetStateAction<bigint | undefined>>;
    setMarketOpenState: Dispatch<SetStateAction<EMarketTimingState>>;
    setContractVersion: Dispatch<SetStateAction<EContractVersion | undefined>>;
    setSelectedAssetA: Dispatch<SetStateAction<IMappedAsset<IAssetMetaData> | undefined>>;
    setSelectedAssetB: Dispatch<SetStateAction<IMappedAsset<IAssetMetaData> | undefined>>;
    setSelectedFeeTier: Dispatch<SetStateAction<EFeeTiers | undefined>>;
    setShowInitialAssetSelect: Dispatch<SetStateAction<boolean>>;
    setValueAssetA: Dispatch<SetStateAction<string>>;
    setValueAssetB: Dispatch<SetStateAction<string>>;
    setWizardStep: Dispatch<SetStateAction<ECreatePoolWizardStep>>;
  };
  state: {
    fee: bigint;
    marketOpen?: bigint;
    marketTimingState: EMarketTimingState;
    contractVersion: EContractVersion | undefined;
    selectedAssetA?: IMappedAsset<IAssetMetaData>;
    selectedAssetB?: IMappedAsset<IAssetMetaData>;
    selectedFeeTier?: EFeeTiers;
    showInitialAssetSelect: boolean;
    valueAssetA: string;
    valueAssetB: string;
    wizardStep: ECreatePoolWizardStep;
  };
}

const defaultCreatePoolContext: ICreatePoolContext = {
  derived: {
    aPerB: "0",
    amountOfLpTokens: undefined,
    assetAmountA: new AssetAmount(0n),
    assetAmountB: new AssetAmount(0n),
    bPerA: "0",
    isFetchingPoolsByPair: false,
    multiplePoolsExistForPair: false,
    poolAlreadyExistsForFeeTier: false,
    somePoolsExistForPair: false,
    anyPoolExitForPair: false,
  },
  handlers: {
    handleBackToInitialAssetSelect: () => {},
    handleCloseCreatePoolDialog: () => {},
    setFee: () => {},
    setMarketOpen: () => {},
    setMarketOpenState: () => {},
    setContractVersion: () => {},
    setSelectedAssetA: () => {},
    setSelectedAssetB: () => {},
    setSelectedFeeTier: () => {},
    setShowInitialAssetSelect: () => {},
    setValueAssetA: () => {},
    setValueAssetB: () => {},
    setWizardStep: () => {},
  },
  state: {
    fee: 30n,
    marketOpen: undefined,
    marketTimingState: EMarketTimingState.default,
    contractVersion: undefined,
    selectedAssetA: undefined,
    selectedAssetB: undefined,
    showInitialAssetSelect: true,
    valueAssetA: "",
    valueAssetB: "",
    wizardStep: ECreatePoolWizardStep.entry,
  },
};

export const CreatePoolContext = createContext<ICreatePoolContext>(defaultCreatePoolContext);

export const useCreatePoolContext = () => useContext(CreatePoolContext);

const CreatePoolProvider: FC<PropsWithChildren> = ({ children }) => {
  const { dispatch: appContextDispatch } = useAppContext();
  const {
    handlers: { setLiquidityFlowState },
  } = useLiquidityContext();

  /**
   * Local States accounting for the `CreatePool` flow
   */
  const [selectedAssetA, setSelectedAssetA] = useState<IMappedAsset<IAssetMetaData>>();
  const [selectedAssetB, setSelectedAssetB] = useState<IMappedAsset<IAssetMetaData>>();
  const [fee, setFee] = useState<ICreatePoolContext["state"]["fee"]>(
    defaultCreatePoolContext.state.fee
  );
  const [marketOpen, setMarketOpen] = useState<bigint>();
  const [marketTimingState, setMarketTimingState] = useState<EMarketTimingState>(
    defaultCreatePoolContext.state.marketTimingState
  );
  const [selectedFeeTier, setSelectedFeeTier] = useState<EFeeTiers>();
  const [showInitialAssetSelect, setShowInitialAssetSelect] = useState(true);
  const [valueAssetA, setValueAssetA] = useState("");
  const [valueAssetB, setValueAssetB] = useState("");
  const [wizardStep, setWizardStep] = useState<ECreatePoolWizardStep>(
    defaultCreatePoolContext.state.wizardStep
  );
  // Determine the contract version
  const [contractVersion, setContractVersion] = useState(EContractVersion.V3);

  const { coinA, coinB } = useMemo(() => {
    if (!selectedAssetA || !selectedAssetB) return {};

    const assets: [AssetAmount<IAssetAmountMetadata>, AssetAmount<IAssetAmountMetadata>] = [
      selectedAssetA.quantity,
      selectedAssetB.quantity,
    ];

    const sortedAssets = SundaeUtils.sortSwapAssetsWithAmounts(assets);
    return {
      coinA: sortedAssets[0].metadata.assetId,
      coinB: sortedAssets[1].metadata.assetId,
    };
  }, [selectedAssetA, selectedAssetB]);

  // Fetch available Pools for the given pair --> Used for `Create Pool` & `Add Liquidity` flows
  const {
    data: dataPoolsByPair,
    isLoading: isFetchingPoolsByPair,
    isRefetching: isRefetchingPoolsByPair,
    isFetched,
  } = usePoolsByPair({ coinA, coinB });

  // Filter the pools by the contract version
  const { v1Pools, v3Pools } = useMemo(() => {
    return {
      v1Pools: dataPoolsByPair.stats.filter((pool) => pool.version === EContractVersion.V1),
      v3Pools: dataPoolsByPair.stats.filter((pool) => pool.version === EContractVersion.V3),
    };
  }, [dataPoolsByPair.stats]);

  /**
   * Asset Amounts for the inputs given by the user.
   */
  const { assetAmountA, assetAmountB } = useMemo(() => {
    const assetAmountA = AssetAmount.fromValue(valueAssetA, selectedAssetA?.metadata);
    const assetAmountB = AssetAmount.fromValue(valueAssetB, selectedAssetB?.metadata);
    return { assetAmountA, assetAmountB };
  }, [selectedAssetA, selectedAssetB, valueAssetA, valueAssetB]);

  /**
   * Some pools for this pair already exist
   */
  const somePoolsExistForPairV1 = v1Pools.length > 0;
  const somePoolsExistForPairV3 = v3Pools.length > 0;
  const multiplePoolsExistForPairV1 = v1Pools.length > 1;
  const multiplePoolsExistForPairV3 = v3Pools.length > 1;
  const anyPoolExitForPair = !somePoolsExistForPairV1 && !somePoolsExistForPairV3;

  /**
   * Pool already exists for the selected fee tier
   */
  const poolAlreadyExistsForFeeTierV1 = useMemo(
    () => v1Pools.some(({ fee }) => Number(fee) === Number(selectedFeeTier)),
    [dataPoolsByPair.stats, selectedFeeTier]
  );
  const poolAlreadyExistsForFeeTierV3 = useMemo(
    () => v3Pools.some(({ fee }) => Number(fee) === Number(selectedFeeTier)),
    [dataPoolsByPair.stats, selectedFeeTier]
  );

  const { somePoolsExistForPair, multiplePoolsExistForPair, poolAlreadyExistsForFeeTier } =
    useMemo(() => {
      if (contractVersion === EContractVersion.V1) {
        return {
          somePoolsExistForPair: somePoolsExistForPairV1,
          multiplePoolsExistForPair: multiplePoolsExistForPairV1,
          poolAlreadyExistsForFeeTier: poolAlreadyExistsForFeeTierV1,
        };
      }

      return {
        somePoolsExistForPair: somePoolsExistForPairV3,
        multiplePoolsExistForPair: multiplePoolsExistForPairV3,
        poolAlreadyExistsForFeeTier: poolAlreadyExistsForFeeTierV3,
      };
    }, [
      contractVersion,
      multiplePoolsExistForPairV1,
      multiplePoolsExistForPairV3,
      poolAlreadyExistsForFeeTierV1,
      poolAlreadyExistsForFeeTierV3,
      somePoolsExistForPairV1,
      somePoolsExistForPairV3,
    ]);

  /**
   * Calculate Asset Ratios
   */
  const { aPerB, bPerA } = useMemo(() => {
    const ratioA = new AssetRatio(assetAmountA, assetAmountB).getLimitPrice();
    const ratioB = new AssetRatio(assetAmountB, assetAmountA).getLimitPrice();

    if (
      !Number.isNaN(ratioA) &&
      Number.isFinite(ratioA) &&
      !Number.isNaN(ratioB) &&
      Number.isFinite(ratioB)
    ) {
      return { aPerB: String(ratioA), bPerA: String(ratioB) };
    }

    return { aPerB: "0", bPerA: "0" };
  }, [assetAmountA, assetAmountB]);

  /**
   * Calculated amount of LP Tokens for this pool --> Either when a Pool is created or whenever a user adds new Liquidity to an existing Pool.
   */
  const amountOfLpTokens = useMemo(
    () => getFirstLp(assetAmountA.amount, assetAmountB.amount),
    [assetAmountA.amount, assetAmountB.amount]
  );

  /**
   * If the user clicks on the `Back` button while in the `CreatePool` flow, we need to reset the state and show the initial asset select UI.
   */
  const handleBackToInitialAssetSelect = useCallback(() => {
    setShowInitialAssetSelect(true);
    setLiquidityFlowState(ELiquidityFlowState.reset);
  }, [setLiquidityFlowState]);

  /**
   * If the user closes the `CreatePool` dialog, we need to reset the state and show the initial asset select UI.
   */
  const handleCloseCreatePoolDialog = useCallback(() => {
    setShowInitialAssetSelect(true);
    setLiquidityFlowState(ELiquidityFlowState.reset);
    setSelectedAssetA(undefined);
    setSelectedAssetB(undefined);
    Actions.setDialogState({ dialog: "liquidity.createPool", isOpen: false }, appContextDispatch);
  }, [appContextDispatch]);

  /**
   * If there is no selected fee tier preselected, we default to the most popular one (0.3%).
   */
  useEffect(() => {
    if (!selectedFeeTier) {
      setSelectedFeeTier(EFeeTiers.POPULAR);
    }
  }, [selectedFeeTier]);

  return (
    <CreatePoolContext.Provider
      value={{
        derived: {
          amountOfLpTokens,
          aPerB,
          assetAmountA,
          assetAmountB,
          bPerA,
          isFetchingPoolsByPair: (isFetchingPoolsByPair || isRefetchingPoolsByPair) && !isFetched,
          multiplePoolsExistForPair,
          poolAlreadyExistsForFeeTier,
          somePoolsExistForPair,
          anyPoolExitForPair,
        },
        handlers: {
          handleBackToInitialAssetSelect,
          handleCloseCreatePoolDialog,
          setFee,
          setMarketOpen,
          setMarketOpenState: setMarketTimingState,
          setContractVersion,
          setSelectedAssetA,
          setSelectedAssetB,
          setSelectedFeeTier,
          setShowInitialAssetSelect,
          setValueAssetA,
          setValueAssetB,
          setWizardStep,
        },
        state: {
          fee,
          marketOpen,
          marketTimingState,
          contractVersion,
          selectedAssetA,
          selectedAssetB,
          selectedFeeTier,
          showInitialAssetSelect,
          valueAssetA,
          valueAssetB,
          wizardStep,
        },
      }}
    >
      {children}
    </CreatePoolContext.Provider>
  );
};

export default CreatePoolProvider;
